<template>
    <v-container fluid>
        <!-- Income title -->
        <v-col :alvqa="alvqa + '-title'" class="mb-2">
            <v-row class="pl-4 headline" align="center">
                {{ item.fileName }} 
                ({{ $t('lang_PUBLISHING_INCOME_CAPTION_WORKS_WITH_ERRORS') }}: {{ item.totalRecords }} | {{ $t('lang_GENERAL_CAPTION_NUMBER_OF_ERRORS') }}: {{ item.numberOfTotalRows }})
                <ContextHelpIcon 
                    v-if="hasContextHelp"
                    context="PublishingIncomeErrorList"
                    :alvqa="alvqa + '-context-help-icon'"
                    class="ml-2"
                    :yOffset="-2"
                />
            </v-row>
        </v-col>

        <v-row class="pl-4 pr-4">
            <v-col>
                <v-data-table
                    :headers="headers"
                    hide-default-footer
                    :items="errorList"
                    :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                    style="background-color: var(--v-expandPanelBackground-base);"
                    class="noMinWidth"
                    :header-props="{ sortI: '' }"
                    :items-per-page="itemsPerPage"
                >
                    <!-- alvqas for headers -->
                    <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                        <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                    </template>
                    <!-- error code filter in header -->
                    <template v-slot:[`header.error`]>
                        <v-menu
                            v-model="errorFilterMenu"
                            :close-on-content-click="false"
                            offset-y
                            left
                            min-width="220"
                            max-height="320"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <span :alvqa="alvqa + '-error-list-header-error'">
                                    {{ $t('lang_PUBLISHING_INCOME_CAPTION_ERROR') }}
                                </span>
                                <v-icon
                                    v-on="on"
                                    v-bind="attrs"
                                    small
                                    class="ml-1"
                                    :color="selectedErrorCodes.length ? 'primary' : 'grey lighten-1'"
                                    @click.stop
                                >
                                    fa-filter
                                </v-icon>
                            </template>

                            <v-card>
                                <v-list dense>
                                    <v-list-item>
                                        <v-list-item-action>
                                            <v-checkbox
                                                :input-value="selectedErrorCodes.length === errorCodes.length"
                                                :indeterminate="selectedErrorCodes.length > 0 && selectedErrorCodes.length < errorCodes.length"
                                                @change="toggleAllErrorCodes"
                                                dense
                                                hide-details
                                            />
                                        </v-list-item-action>
                                        <v-list-item-content>
                                            <v-list-item-title class="font-weight-regular">
                                                {{ $t('lang_GENERIC_CAPTION_SELECT_ALL') }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                    <v-divider />
                                    <v-list-item
                                        v-for="code in errorCodes"
                                        :key="code"
                                        dense
                                    >
                                        <v-list-item-action>
                                            <v-checkbox
                                                v-model="selectedErrorCodes"
                                                :value="code"
                                                dense
                                                hide-details
                                            />
                                        </v-list-item-action>
                                        <v-list-item-content>
                                            <v-list-item-title class="font-weight-regular">
                                                {{ $t(errorCodeTranslations[code] ? errorCodeTranslations[code] : code) }}
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                                <v-divider />
                                <v-card-actions>
                                    <v-spacer />
                                    <v-btn text small @click="selectedErrorCodes = []">
                                        {{ $t('lang_GENERIC_CAPTION_CLEAR') }}
                                    </v-btn>
                                    <v-btn text small color="primary" @click="errorFilterMenu = false">
                                        {{ $t('lang_GENERAL_BUTTON_CLOSE') }}
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-menu>
                    </template>

                    <!-- cell content -->
                    <template v-slot:[`item.title`]="{ item }">
                       <div class="ellipsis">
                           <BaseTooltip 
                                :simpleText="item.title +' (' + $t('lang_PUBLISHING_INCOME_CAPTION_ERRORS') + ': '+ item.numberOfRows +')'"
                                :alvqa="alvqa +'-error-list-text-title'"
                           />
                        </div>
                    </template>
                    <template v-slot:[`item.composers`]="{ item }">
                       <div class="ellipsis">
                           <BaseTooltip 
                                :simpleText="formatComposers(item.composers)"
                                :alvqa="alvqa +'-error-list-text-composers'"
                           />
                        </div>
                    </template>
                    <template v-slot:[`item.workIdentifier`]="{ item }">
                        <div class="work-id-cell" :alvqa="alvqa +'-error-list-text-work-id'">
                            <span class="ellipsis">{{ item.workIdentifier }}</span>
                            <BaseIcon
                                v-if="showGemaRepertoireIcon(item)"
                                class="ml-1"
                                icon="link"
                                small
                                tooltipText="lang_PUBLISHING_WORKS_CAPTION_GEMA_REPERTOIRE_TOOLTIP"
                                :alvqa="alvqa + '-icon-gema-repertoire'"
                                @click.stop="openGemaRepertoire(item)"
                            />
                        </div>
                    </template>
                    <template v-slot:[`item.collPercent`]="{ item }">
                       <div 
                            class="text-right"
                            :alvqa="alvqa +'-error-list-text-coll-percent'"
                        >
                            {{ $alvPlugin.getNumberLocalization(item.collPercent, 'number') }}
                        </div>
                    </template>
                    <template v-slot:[`item.info`]="{ item }">
                        <div 
                            class="ellipsis"
                            :alvqa="alvqa +'-error-list-text-info'"
                        >
                            <BaseTooltip 
                                :simpleText="makeInfoCaption(item)"
                            />
                        </div>
                    </template>
                    <template v-slot:[`item.netAmount`]="{ item }">
                        <div 
                            class="text-right"
                            :alvqa="alvqa +'-error-list-text-net-amount'"
                        >
                            {{ $alvPlugin.formatMoney(item.netAmount) }}
                        </div>
                    </template>
                    <template v-slot:[`item.error`]="{ item }">
                        <div :alvqa="alvqa +'-error-list-text-error'" class="error-cell">
                            <BaseIcon
                                v-if="item.errorType == 'VF102' || item.errorType == 'VF108'"
                                @click.stop="fixIt(item)"
                                icon="fa-wrench"
                                color="standardBrownIcon"
                                small="small"
                                tooltipText="lang_GENERIC_CAPTION_FIX_IT"
                                :alvqa="alvqa + '-icon-fix-it-' + item.errorType"
                            />
                            <BaseIcon
                                v-else-if="item.errorType == 'SF102'"
                                @click.stop="fixItSF102(item)"
                                icon="fa-wrench"
                                color="standardBrownIcon"
                                small="small"
                                tooltipText="lang_GENERIC_CAPTION_FIX_IT"
                                :alvqa="alvqa + '-icon-fix-it-' + item.errorType"
                            />
                            <BaseIcon
                                v-else-if="checkC3ErrorFixIt(item)"
                                @click.stop="fixItC3Error(item)"
                                icon="fa-wrench"
                                color="standardBrownIcon"
                                small="small"
                                tooltipText="lang_GENERIC_CAPTION_FIX_IT"
                                :alvqa="alvqa + '-icon-fix-it-work-not-found'"
                            />
                            <BaseIcon
                                v-else-if="checkRecipientErrorFixIt(item)"
                                @click.stop="fixItRecipient(item)"
                                icon="fa-wrench"
                                color="standardBrownIcon"
                                small="small"
                                tooltipText="lang_GENERIC_CAPTION_FIX_IT"
                                :alvqa="alvqa + '-icon-fix-it-recipient-not-matching'"
                            />

                            <v-tooltip bottom color="tooltipBackground">
                                <template v-slot:activator="{ on }">
                                    <span class="error-text" v-on="on">{{ getErrorMessage(item) }}</span>
                                </template>
                                <span>{{ getErrorMessage(item) }}</span>
                            </v-tooltip>

                            <BaseIcon
                                icon="auto_fix_high"
                                color="iconStandard"
                                :tooltipText="$t('lang_PUBLISHING_INCOME_CAPTION_AI_ANALYSIS')"
                                :alvqa="alvqa + '-icon-ai-analysis'"
                                @click.stop="openAiAnalysis(item)"
                            />
                        </div>
                    </template>
                </v-data-table>
                <v-pagination   
                    v-if="totalErrorListPages > 1"
                    total-visible="10" 
                    v-model="currentErrorListPage" 
                    :length="totalErrorListPages"
                    color="pagination"
                    @input="nextPage()"
            ></v-pagination>
            </v-col>
        </v-row>

        <GemaRepertoireDialog
            ref="gemaRepertoireDialog"
            :show="showGemaRepertoireDialog"
            :work-id="gemaRepertoireWorkId"
            :alvqa="alvqa + '-gema-repertoire-dialog'"
            @close="closeGemaRepertoireDialog"
        />
    </v-container>
</template>

<script>
import BaseTooltip from '@/components/_base/BaseTooltip'
import BaseIcon from '@/components/_buttons/BaseIcon'
import ContextHelpIcon from '@/components/_buttons/ContextHelpIcon'
import GemaRepertoireDialog from '@/components/_dialogs/GemaRepertoireDialog'

export default {
    name: "IncomeReadOnly",
    components: {
        BaseTooltip,
        BaseIcon,
        ContextHelpIcon,
        GemaRepertoireDialog,
    },
    props: {
        itemId: {
            type: Number
        }
    },
    data() {
        return {
            alvqa: 'publishing-income-list-row-' +  this.itemId + '-read-only',
            hasContextHelp: false,
            selectedErrorCodes: [],
            errorFilterMenu: false,
            currentErrorListPage: 1,
            showGemaRepertoireDialog: false,
            gemaRepertoireWorkId: '',
        }
    },
    computed: {
        item() { 
            return this.$store.getters.getIncomeDetails
        },
        errorList() {
            return this.$store.getters.getIncomeDetails.list ? this.$store.getters.getIncomeDetails.list : []
        },
        expandedItem() { 
            return this.$store.getters.getIncome.find(file => file.id === this.itemId)
        },
        headers() {
            return [
                {
                    text: this.$t("lang_PUBLISHING_WORKS_CAPTION_WORK_TITLE"),
                    value: "title", 
                    align: "left", 
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + "-error-list-header-work-title"
                },
                {
                    text: this.$t("lang_PUBLISHING_CAPTION_COMPOSERS"),
                    value: "composers", 
                    align: "left", 
                    class: 'text-no-wrap',
                    width: '160px',
                    sortable: false,
                    alvqa: this.alvqa + "-error-list-header-composers"
                },
                {
                    text: this.$t("lang_PUBLISHING_WORKS_CAPTION_WORK_ID"),
                    value: "workIdentifier", 
                    align: "left", 
                    class: 'text-no-wrap',
                    width: "180px",
                    sortable: false,
                    alvqa: this.alvqa + "-error-list-header-work-id"
                },
                {
                    text: this.$t("lang_PUBLISHING_INCOME_CAPTION_COLL_PERCENT"),
                    value: "collPercent", 
                    align: "right", 
                    class: 'text-no-wrap',
                    width: "100px",
                    sortable: false,
                    alvqa: this.alvqa + "-error-list-header-coll-percent"
                },
                {
                    text: this.$t("lang_PUBLISHING_INCOME_CAPTION_INFO"),
                    value: "info", 
                    align: "left", 
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + "-error-list-header-info"
                },
                {
                    text: this.$t("lang_PUBLISHING_INCOME_CAPTION_NET") + ' ' + this.$store.getters.getSelectedCurrency,
                    value: "netAmount", 
                    align: "right", 
                    class: 'text-no-wrap',
                    width: "120px",
                    sortable: false,
                    alvqa: this.alvqa + "-error-list-header-net-amount"
                },
                {
                    text: this.$t("lang_PUBLISHING_INCOME_CAPTION_ERROR"),
                    value: "error", 
                    align: "left", 
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + "-error-list-header-error"
                },
            ]
        },
        itemsPerPage() { return this.$store.getters.getItemsPerPage },
        errorCodes() { return this.item.errorCodes },
        totalErrorListPages() {
            return this.item.numberOfPages ? this.item.numberOfPages : 1
        },
        errorCodeTranslations() {
            return this.$store.getters.getIncomeErrorCodes
        }
    },
    methods: {
        showGemaRepertoireIcon(item) {
            if (!this.expandedItem || this.expandedItem.source !== 'GEMA') return false
            const workId = item && item.workIdentifier != null ? String(item.workIdentifier).trim() : ''
            return /^\d{1,10}-\d{3}$/.test(workId)
        },
        openGemaRepertoire(item) {
            const workId = String(item.workIdentifier).trim()
            const alreadyOpen = this.showGemaRepertoireDialog && this.gemaRepertoireWorkId === workId
            this.gemaRepertoireWorkId = workId
            if (this.showGemaRepertoireDialog) {
                if (!alreadyOpen && this.$refs.gemaRepertoireDialog) {
                    this.$nextTick(() => this.$refs.gemaRepertoireDialog.load())
                }
            } else {
                this.showGemaRepertoireDialog = true
            }
        },
        closeGemaRepertoireDialog() {
            this.showGemaRepertoireDialog = false
            this.gemaRepertoireWorkId = ''
        },
        formatComposers(composers) {
            if (!composers) return ''
            return String(composers).split('|').map(c => c.trim()).filter(Boolean).join(' | ')
        },
        checkC3ErrorFixIt(item) {
            const isWorkNotFound = ['lang_PUBLISHING_STATEMENTS_C3_MESSAGE_ERROR_WORK_NOT_FOUND', 'lang_PUBLISHING_STATEMENTS_C3_MESSAGE_ERROR_MULTIPLE_WORKS_FOUND'].includes(item.error);
            const hasWorkIdentifier = item.workIdentifier && item.workIdentifier !== '' && item.workIdentifier !== '-0';
            const hasSourceAlvId = this.expandedItem && this.expandedItem.sourceAlvId && this.expandedItem.sourceAlvId !== '';
            
            return isWorkNotFound && hasWorkIdentifier && hasSourceAlvId;
        },
        fixIt(item) {
            this.$store.commit('setShowFixItDialog', true)
            this.$store.commit('setFixItErrorObject', item)
        },
        fixItSF102(item) {
            this.$store.commit('setFixItSF102ErrorObject', item)
            if (item.title) {
                this.$store.commit('setWorksForFixItSearch', item.title);
            }
            this.$store.commit('setShowFixItSF102Dialog', true)
        },
        fixItC3Error(item) {
            this.$store.commit('setFixItC3ErrorObject', item)
            if (item.title) {
                this.$store.commit('setWorksForFixItSearch', item.title);
            }
            this.$store.commit('setShowFixItC3ErrorDialog', true)
        },
        checkRecipientErrorFixIt(item) {
            const isRecipientNotMatching = item.error === 'lang_PUBLISHING_STATEMENTS_C3_MESSAGE_ERROR_RECIPIENT_NOT_MATCHING';
            const hasRecipientId = item.recipientId && item.recipientId !== '';
            const hasSourceAlvId = this.expandedItem && this.expandedItem.sourceAlvId && this.expandedItem.sourceAlvId !== '';
            
            return isRecipientNotMatching && hasRecipientId && hasSourceAlvId;
        },
        fixItRecipient(item) {
            this.$store.commit('setFixItRecipientErrorObject', item)
            this.$store.commit('setShowFixItRecipientDialog', true)
        },
        openAiAnalysis(item) {
            this.$store.dispatch('fetchIncomeErrorAiAnalysis', item.alvId)
        },
        makeInfoCaption(item) {
            let caption = ""
            if (item.recipientId) {
                caption += this.$t('lang_GENERIC_CAPTION_RECIPIENT') + ': ' + item.recipientId
            }
            if (item.rightsType) {
                if (caption.length > 0) caption += ', '
                caption += this.$t('lang_GENERIC_CAPTION_RIGHTS_TYPE') + ': ' + item.rightsType
            }
            if (item.category) {
                if (caption.length > 0) caption += ', '
                caption += this.$t('lang_GENERIC_CAPTION_CATEGORY') + ': ' + item.category
            }
            if (item.subCategory) {
                if (caption.length > 0) caption += ', '
                caption += this.$t('lang_GENERIC_CAPTION_SUBCATEGORY') + ': ' + item.subCategory
            }
            if (item.memo) {
                if (caption.length > 0) caption += ', '
                caption += this.$t('lang_GENERIC_CAPTION_MEMO') + ': ' + item.memo
            }

            return caption
        },
        getErrorMessage(item) {
            const error = item.error

            if (!Array.isArray(error)) {
                return this.$t(error)
            }

            let translated = []
            for (let i = 0; i < error.length; i++) {
                translated.push(this.$t(error[i]))
            }

            return translated.join(' ')
        },
        toggleAllErrorCodes(checked) {
            this.selectedErrorCodes = checked ? [...this.errorCodes] : []
        },
        nextPage() {
            this.$store.dispatch('fetchIncomeDetails', { alvId: this.item.alvId, currentPage: this.currentErrorListPage })
        }
    },
    watch: {
        selectedErrorCodes(newValue) {
            this.$store.commit('setIncomeErrorListSelectedErrorCodes', newValue)
            this.currentErrorListPage = 1
            this.$store.dispatch('fetchIncomeDetails', { alvId: this.item.alvId })
        }
    },
    async created() {
        this.hasContextHelp = await this.$alvPlugin.checkContextHelpAvailability('PublishingIncomeErrorList')
    }
}
</script>

<style>
    .noMinWidth table {
        min-width: 0px !important;
        table-layout: fixed;
    }

    .error-cell {
        display: flex;
        align-items: center;
        gap: 4px;
        flex-wrap: nowrap;
    }
    .error-cell .error-text {
        flex: 1 1 auto;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    /* Vuetify v-btn icon has default 36px width — shrink so wrench/AI icons don't squeeze the text */
    .error-cell .v-btn--icon.v-size--default {
        width: 28px;
        height: 28px;
    }
    .work-id-cell {
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
        min-width: 0;
    }
    .work-id-cell .ellipsis {
        min-width: 0;
    }
    .work-id-cell .v-btn--icon.v-size--default {
        width: 28px;
        height: 28px;
        flex: 0 0 auto;
    }
</style>